// src/pages/admin/Dashboard.jsx
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import AdminSidebar from '../../components/admin/AdminSidebar';
import DashboardStats from '../../components/admin/DashboardStats';
import { getServices, getProjects, getMedia, getCertifications } from '../../utils/api';
import { 
  FaCogs, 
  FaProjectDiagram, 
  FaImages, 
  FaCertificate,
  FaPlus,
  FaEdit,
  FaTrash,
  FaRobot
} from 'react-icons/fa';

const Dashboard = () => {
  const [stats, setStats] = useState({
    services: 0,
    projects: 0,
    media: 0,
    certifications: 0,
    recentServices: [],
    recentProjects: []
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [services, projects, media, certs] = await Promise.all([
        getServices(),
        getProjects(),
        getMedia(),
        getCertifications()
      ]);

      setStats({
        services: services.length,
        projects: projects.length,
        media: media.length,
        certifications: certs.length,
        recentServices: services.slice(0, 5),
        recentProjects: projects.slice(0, 5)
      });
    } catch (error) {
      console.error('Error loading dashboard data:', error);
    }
  };

  const quickActions = [
    { icon: FaPlus, label: 'Add Service', path: '/admin/services', color: 'blue' },
    { icon: FaProjectDiagram, label: 'Add Project', path: '/admin/projects', color: 'green' },
    { icon: FaImages, label: 'Upload Media', path: '/admin/media', color: 'purple' },
    { icon: FaCertificate, label: 'Add Certification', path: '/admin/certifications', color: 'orange' },
    { icon: FaRobot, label: 'AI Settings', path: '/admin/ai-settings', color: 'indigo' }
  ];

  return (
    <div className="flex min-h-screen bg-gray-100">
      <AdminSidebar />
      
      <div className="flex-1 p-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-800">Admin Dashboard</h1>
          <p className="text-gray-600">Welcome to Brasotech Investments Admin Panel</p>
        </div>

        {/* Stats Cards */}
        <DashboardStats stats={stats} />

        {/* Quick Actions */}
        <div className="mb-8">
          <h2 className="text-xl font-semibold text-gray-800 mb-4">Quick Actions</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {quickActions.map((action, index) => (
              <Link
                key={index}
                to={action.path}
                className={`bg-white p-6 rounded-lg shadow hover:shadow-md transition-shadow duration-200 border-l-4 border-${action.color}-500`}
              >
                <div className="flex items-center space-x-4">
                  <div className={`p-3 bg-${action.color}-100 rounded-lg`}>
                    <action.icon className={`h-6 w-6 text-${action.color}-600`} />
                  </div>
                  <span className="font-medium text-gray-800">{action.label}</span>
                </div>
              </Link>
            ))}
          </div>
        </div>

        {/* Recent Content */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Recent Services */}
          <div className="bg-white rounded-lg shadow">
            <div className="px-6 py-4 border-b flex justify-between items-center">
              <h2 className="text-xl font-semibold text-gray-800">Recent Services</h2>
              <Link to="/admin/services" className="text-blue-600 hover:text-blue-800 text-sm font-medium">
                View All
              </Link>
            </div>
            <div className="p-6">
              {stats.recentServices.length > 0 ? (
                <div className="space-y-4">
                  {stats.recentServices.map(service => (
                    <div key={service.id} className="flex items-center justify-between p-3 hover:bg-gray-50 rounded">
                      <div>
                        <h4 className="font-medium text-gray-800">{service.title}</h4>
                        <p className="text-sm text-gray-600">{service.category}</p>
                      </div>
                      <div className="flex space-x-2">
                        <Link
                          to={`/admin/services?edit=${service.id}`}
                          className="text-blue-600 hover:text-blue-800 p-2"
                        >
                          <FaEdit className="h-4 w-4" />
                        </Link>
                        <button className="text-red-600 hover:text-red-800 p-2">
                          <FaTrash className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500 text-center py-4">No services added yet</p>
              )}
            </div>
          </div>

          {/* Recent Projects */}
          <div className="bg-white rounded-lg shadow">
            <div className="px-6 py-4 border-b flex justify-between items-center">
              <h2 className="text-xl font-semibold text-gray-800">Recent Projects</h2>
              <Link to="/admin/projects" className="text-blue-600 hover:text-blue-800 text-sm font-medium">
                View All
              </Link>
            </div>
            <div className="p-6">
              {stats.recentProjects.length > 0 ? (
                <div className="space-y-4">
                  {stats.recentProjects.map(project => (
                    <div key={project.id} className="flex items-center justify-between p-3 hover:bg-gray-50 rounded">
                      <div>
                        <h4 className="font-medium text-gray-800">{project.title}</h4>
                        <p className="text-sm text-gray-600">{project.status}</p>
                      </div>
                      <div className="flex space-x-2">
                        <Link
                          to={`/admin/projects?edit=${project.id}`}
                          className="text-blue-600 hover:text-blue-800 p-2"
                        >
                          <FaEdit className="h-4 w-4" />
                        </Link>
                        <button className="text-red-600 hover:text-red-800 p-2">
                          <FaTrash className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500 text-center py-4">No projects added yet</p>
              )}
            </div>
          </div>
        </div>

        {/* System Info */}
        <div className="mt-8 bg-white rounded-lg shadow p-6">
          <h2 className="text-xl font-semibold text-gray-800 mb-4">System Information</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="p-4 bg-gray-50 rounded">
              <h3 className="font-medium text-gray-700">Frontend Version</h3>
              <p className="text-sm text-gray-600">v1.0.0</p>
            </div>
            <div className="p-4 bg-gray-50 rounded">
              <h3 className="font-medium text-gray-700">Last Updated</h3>
              <p className="text-sm text-gray-600">{new Date().toLocaleDateString()}</p>
            </div>
            <div className="p-4 bg-gray-50 rounded">
              <h3 className="font-medium text-gray-700">Total Content Items</h3>
              <p className="text-sm text-gray-600">
                {stats.services + stats.projects + stats.media + stats.certifications}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;